/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useWBL, getUserRegions, resolveUserTenant } from '../../context/WBLContext';
import { Cpu, GraduationCap, HeartPulse, LogOut, ShieldAlert, LayoutDashboard, Briefcase, FileText, CheckCircle, Star, Calendar, BarChart3, Mail, User, Settings, HelpCircle, Users, Menu, ChevronDown } from 'lucide-react';
import { StudentAvatar } from './StudentAvatar';

export const Header: React.FC = () => {
  const { activeTenant, activeUser, signOutUser, setCurrentView, globalBranding, messages, tenants, changeTenant, schools, allSchools, users } = useWBL();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const regionDropdownRef = useRef<HTMLDivElement>(null);

  const unreadMessagesCount = (messages || []).filter(m => !m.read && !m.deleted).length;

  const advisorHasStudents = useMemo(() => {
    if (!activeUser || activeUser.role !== 'advisor') return true;
    if (!activeUser.verified) return false;

    const referenceIds = Array.from(new Set([
      activeUser.schoolId,
      ...(activeUser.schoolsServedIds || []),
      activeUser.school,
      ...(activeUser.schoolsServed || [])
    ].filter(Boolean) as string[]));

    const schoolPool = allSchools && allSchools.length > 0 ? allSchools : schools || [];
    const advisorSchools = schoolPool.filter(s => {
      return referenceIds.some(ref => {
        const normRef = ref.toLowerCase().trim();
        const normSchoolName = s.name.toLowerCase().trim();
        const normSchoolId = s.id.toLowerCase().trim();
        return normRef === normSchoolId || normRef === normSchoolName;
      });
    });
    const advisorSchoolIds = advisorSchools.map(s => s.id);
    const advisorSchoolNames = advisorSchools.map(s => s.name.toLowerCase().trim());

    const advisorStudents = (users || []).filter(u => {
      if (u.role !== 'student') return false;
      const ferpaGranted = typeof u.ferpaConsent === 'object'
        ? u.ferpaConsent?.granted === true
        : u.ferpaConsent === true;
      if (!ferpaGranted) return false;

      if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;
      if (u.school && advisorSchoolNames.includes(u.school.toLowerCase().trim())) return true;
      return false;
    });

    return advisorStudents.length > 0;
  }, [users, schools, allSchools, activeUser]);

  const userRegions = useMemo(() => {
    return getUserRegions(activeUser, tenants, allSchools || schools);
  }, [activeUser, tenants, allSchools, schools]);

  const hasMultipleRegions = activeUser?.role === 'superadmin' || userRegions.length > 1;

  const singleRegionName = useMemo(() => {
    if (!activeUser) return activeTenant.name || 'NORTHEAST REGION';
    if (userRegions.length === 1) return userRegions[0].name;
    const resolved = resolveUserTenant(activeUser, tenants, allSchools || schools);
    if (resolved && resolved.id !== 'global') return resolved.name;
    if (activeTenant.id !== 'global' && activeTenant.name) return activeTenant.name;
    if (activeUser.region && activeUser.region.toLowerCase() !== 'global') return activeUser.region;
    return 'SPARTANBURG COUNTY';
  }, [activeUser, userRegions, activeTenant, tenants, allSchools, schools]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target as Node)) {
        setIsRegionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="portal-header-nav w-full bg-primary border-b border-primary/20 px-4 md:px-8 py-3.5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-sans relative z-40">
      
      {/* Brand Group */}
      <div className="tenant-branding-banner flex items-center space-x-3 cursor-pointer" onClick={() => setCurrentView('dashboard')}>
        {globalBranding?.logos?.navigation ? (
          <img 
            src={globalBranding.logos.navigation} 
            alt="Navigation Logo" 
            className="h-10 max-w-[200px] object-contain transition-all duration-300 hover:opacity-90" 
            referrerPolicy="no-referrer"
          />
        ) : (
          <>
            <div className="h-10 w-10 text-white rounded-xl shadow-sm flex items-center justify-center bg-secondary transition-all duration-300">
              {activeTenant.logo === 'Cpu' && <Cpu className="w-5.5 h-5.5" />}
              {activeTenant.logo === 'GraduationCap' && <GraduationCap className="w-5.5 h-5.5" />}
              {activeTenant.logo === 'HeartPulse' && <HeartPulse className="w-5.5 h-5.5" />}
            </div>
            
            <div>
              <div className="flex items-baseline space-x-2">
                <h1 className="font-extrabold text-white tracking-tight text-base leading-none">
                  {activeTenant.name}
                </h1>
                <span className="text-[10px] bg-white/10 text-white font-bold px-1.5 py-0.5 rounded border border-white/20 uppercase font-mono tracking-wider select-none">
                  portal
                </span>
              </div>
              <div className="text-[10px] text-white/60 mt-1 font-semibold font-mono tracking-tight">
                {typeof window !== 'undefined' ? window.location.host : ''}{activeTenant.id && activeTenant.id !== 'global' && activeTenant.id !== 'apextech' ? `/${activeTenant.id}` : ''}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Profile & Auth actions */}
      {activeUser ? (
        <div className="user-profile-widget flex items-center justify-end space-x-4 relative" ref={dropdownRef}>
          <div className="text-right flex flex-col justify-center">
            <div className="flex items-center justify-end space-x-1.5">
              <span className="text-sm font-extrabold text-white font-sans tracking-tight">{(activeUser.contactFirstName || activeUser.contactLastName) ? `${activeUser.contactFirstName || ''} ${activeUser.contactLastName || ''}`.trim() : activeUser.name}</span>
            </div>
            
            <div className="flex items-center justify-end space-x-1 mt-1 relative" ref={regionDropdownRef}>
              {hasMultipleRegions ? (
                <>
                  <button
                    onClick={() => setIsRegionDropdownOpen(!isRegionDropdownOpen)}
                    className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-white/10 hover:bg-white/20 active:bg-white/30 text-white border border-white/20 transition-all duration-150 flex items-center space-x-1 cursor-pointer select-none focus:outline-none"
                    title="Click to switch your active region context"
                  >
                    <span>{activeTenant.id === 'global' ? 'GLOBAL' : (activeTenant.name || 'REGION')}</span>
                    <ChevronDown className="w-2.5 h-2.5 opacity-80" />
                  </button>

                  {isRegionDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1.5 w-52 bg-white rounded-lg shadow-2xl border border-slate-100 py-1 z-50 text-slate-800 text-left font-sans animate-in fade-in slide-in-from-top-1">
                      <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50/50">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider font-mono">Switch Active Region</span>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <button
                          key="global"
                          onClick={() => {
                            changeTenant('global');
                            setIsRegionDropdownOpen(false);
                          }}
                          className={`w-full px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-50 transition-colors text-left ${
                            activeTenant.id === 'global' ? 'font-black text-[#0094c8] bg-slate-50/50' : 'text-slate-600 font-medium'
                          }`}
                        >
                          <span className="uppercase">Global (All Regions)</span>
                          {activeTenant.id === 'global' && <span className="text-[8px] font-extrabold bg-[#0094c8]/10 text-[#0094c8] px-1.5 py-0.5 rounded uppercase font-mono tracking-wider">Active</span>}
                        </button>
                        {userRegions.map(tenant => {
                          const isActive = tenant.id === activeTenant.id;
                          return (
                            <button
                              key={tenant.id}
                              onClick={() => {
                                changeTenant(tenant.id);
                                setIsRegionDropdownOpen(false);
                              }}
                              className={`w-full px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-50 transition-colors text-left ${
                                isActive ? 'font-black text-[#0094c8] bg-slate-50/50' : 'text-slate-600 font-medium'
                              }`}
                            >
                              <span className="uppercase">{tenant.name}</span>
                              {isActive && <span className="text-[8px] font-extrabold bg-[#0094c8]/10 text-[#0094c8] px-1.5 py-0.5 rounded uppercase font-mono tracking-wider">Active</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider bg-white/10 text-white border border-white/20">
                  {singleRegionName}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 rounded-xl border-2 border-white/20 flex items-center justify-center bg-white text-slate-800 text-base shadow-lg overflow-hidden">
              <StudentAvatar 
                studentId={activeUser.uid} 
                studentName={activeUser.name} 
                studentEmail={activeUser.email}
                profile={activeUser}
              />
            </div>
            
            <button 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="text-white hover:text-secondary-light transition-all p-1 relative"
            >
              <Menu className="w-7 h-7" />
              {unreadMessagesCount > 0 && (
                <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-rose-500 rounded-full border border-primary animate-pulse" />
              )}
            </button>
          </div>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden z-50 text-slate-900 animate-in fade-in slide-in-from-top-2">
              <div className="p-4 border-b border-slate-100">
                <p className="font-bold text-base">{(activeUser.contactFirstName || activeUser.contactLastName) ? `${activeUser.contactFirstName || ''} ${activeUser.contactLastName || ''}`.trim() : activeUser.name}</p>
                <p className="text-xs text-slate-500">{activeUser.email}</p>
              </div>
              
              <div className="py-2">
                {activeUser.role === 'superadmin' && (
                  <button 
                    onClick={() => { setCurrentView('console'); setIsDropdownOpen(false); }} 
                    className="w-full px-4 py-2 hover:bg-slate-50 flex items-center space-x-3 text-sm font-bold text-[#0094c8] transition-colors cursor-pointer border-b border-slate-100 pb-2 mb-1.5"
                    id="header-superadmin-console-link"
                  >
                    <ShieldAlert className="w-5 h-5 text-[#0094c8]" />
                    <span className="font-extrabold text-slate-800">Superadmin Console</span>
                  </button>
                )}
                {activeUser.role === 'regional_admin' && (
                  <button 
                    onClick={() => { setCurrentView('regional_console'); setIsDropdownOpen(false); }} 
                    className="w-full px-4 py-2 hover:bg-slate-50 flex items-center space-x-3 text-sm font-bold text-[#0094c8] transition-colors cursor-pointer border-b border-slate-100 pb-2 mb-1.5"
                    id="header-regional-admin-console-link"
                  >
                    <ShieldAlert className="w-5 h-5 text-[#0094c8]" />
                    <span className="font-extrabold text-slate-800">Regional Admin Console</span>
                  </button>
                )}
                {([
                  { icon: LayoutDashboard, label: 'Dashboard', view: 'dashboard' },
                  { icon: Briefcase, label: 'Opportunities', view: 'opportunities' },
                  { icon: FileText, label: 'Applications', view: 'applications' },
                  { icon: Calendar, label: 'Interviews', view: 'interviews' },
                  { icon: CheckCircle, label: 'Placements', view: 'placements' }
                ]).map(item => (
                  <button 
                    key={item.label} 
                    onClick={() => { 
                      setCurrentView(item.view);
                      setIsDropdownOpen(false); 
                    }} 
                    className="w-full px-4 py-2 hover:bg-slate-50 flex items-center space-x-3 text-sm font-medium cursor-pointer"
                  >
                    <item.icon className="w-4 h-4 text-slate-400" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              
              <div className="py-2 border-t border-slate-100">
                {(() => {
                  const items = [
                    ...(activeTenant.foundationsEnabled !== false || activeUser.role === 'superadmin' ? [{ icon: Star, label: 'Foundations', view: 'foundations' }] : []),
                    ...(activeUser.role === 'superadmin' || activeUser.role === 'regional_admin' || activeUser.role === 'organization' || activeUser.role === 'advisor' ? [{ icon: BarChart3, label: 'Reports', view: 'reports' }] : []),
                    { icon: Mail, label: 'Messages', view: 'messages' },
                  ];
                  
                  if (activeUser.role === 'advisor') {
                    items.unshift({ icon: Users, label: 'Advisees', view: 'advisees' });
                  } else if (activeUser.role === 'student') {
                    items.unshift({ icon: Users, label: 'Advisors', view: 'advisors' });
                  }
                  
                  return items;
                })().map(item => (
                  <button 
                    key={item.label} 
                    onClick={() => {
                      if (item.view) {
                        setCurrentView(item.view);
                        setIsDropdownOpen(false);
                      }
                    }}
                    className={`w-full px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-sm font-medium text-left ${item.view ? 'cursor-pointer' : 'opacity-80'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="w-4 h-4 text-slate-400" />
                      <span>{item.label}</span>
                    </div>
                    {item.view === 'messages' && unreadMessagesCount > 0 && (
                      <span className="text-[10px] font-black tracking-tighter px-2 py-0.5 bg-rose-500 text-white rounded-full">
                        {unreadMessagesCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <div className="py-2 border-t border-slate-100">
                {[
                  { icon: User, label: 'Profile' },
                  { icon: Settings, label: 'Settings' },
                  { icon: HelpCircle, label: 'Help & Tutorials' },
                ].map(item => (
                  <button key={item.label} onClick={() => { setCurrentView(item.label.toLowerCase()); setIsDropdownOpen(false); }} className="w-full px-4 py-2 hover:bg-slate-50 flex items-center space-x-3 text-sm font-medium">
                    <item.icon className="w-4 h-4 text-slate-400" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
              
              <button 
                onClick={signOutUser}
                className="w-full px-4 py-4 hover:bg-red-50 flex items-center space-x-3 text-sm font-bold text-red-600 border-t border-slate-100"
              >
                <LogOut className="w-4 h-4" />
                <span>Log out</span>
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-center space-x-2">
          <span className="text-[11px] text-white/50">Visitor Profile</span>
          <span className="h-2 w-2 rounded-full bg-white/30" />
        </div>
      )}

    </header>
  );
};
